
import { Component, Prop, Watch } from "vue-property-decorator";
import {Getter} from "vuex-class";
import OverPriceSms from "@/components/OverPriceSms.vue";
import Timeout = NodeJS.Timeout;
import { formRules, regex } from "@/utils/formRules";
import GuestInvitationOptions from "@/components/keys/GuestInvitationOptions.vue";
import { mixins } from "vue-class-component";
import DocumentMixin from "@/mixins/DocumentMixin.vue";
import Telephone from "@/components/Telephone.vue";
import SimpleDialog from "@/components/SimpleDialog.vue";
import KeyGuestReminder from "@/components/keys/guests/KeyGuestReminder.vue";

export const DEBOUNCE_TIME_MILLI: number = 500;

@Component({
  components: {
    KeyGuestReminder,
    SimpleDialog,
    GuestInvitationOptions,
    OverPriceSms,
    Telephone,
  },
})
export default class KeyAddGuestDialog extends mixins(DocumentMixin) {

    @Prop({default: null})
    public targetId: number;

    @Prop({default: null})
    public targetType: "keys"|"keyGroups"|"nonTransferables";

    @Prop({default: () => ["user", "contacts"]})
    public userTypes: Array<"user"|"contacts">;

    @Getter private isMobile: boolean;
    @Getter private envCompanyId: number|null;
    @Getter private canDoInEnvCompany: (right: string) => boolean;
    @Getter private currentUser: any;

    private addGuestDialog: boolean = true;
    private loadingAddGuest: boolean = false;
    // select user
    private searchGuestBy: string = null;
    private search: string = null;
    private loadingUsers: boolean = false;
    private timerId: Timeout = null;
    private users: any[] = [];
    private selectedGuestGroup: any = null;
    private newContact: any = {};
    private valid: boolean = null;
    private invitation: any = {};
    private page: number = 1;
    private itemsPerPage: number = 10;
    private totalCount: number = 0;
    // overprice
    private acceptOverPriceDialog: boolean = false;
    private overpriceType: string = null;
    // duplicate / reminder
    private showDuplicateModal: boolean = false;
    private keyGuestReminderDialog: boolean = false;
    private keyGuestReminderUsers: any[] = [];

    private rules: any = formRules;

    @Watch("search")
    @Watch("companyId", {immediate: true})
    public handlerSearch() {
      // Only search if the user hasn't already selected some guest
      if (!this.selectedGuestGroup) {
        if (this.search || this.search === "") {
          // cancel pending call
          clearTimeout(Number(this.timerId));
          // delay new call
          this.loadingUsers = true;
          this.timerId = setTimeout(() => {
            this.getCompanyUsers();
          }, DEBOUNCE_TIME_MILLI) as Timeout;
        } else if (this.envCompanyId) {
          this.getCompanyUsers();
        }
      }
    }

    get isValidForm(): boolean {
      return (this.selectedGuestGroup || this.searchGuestBy === "create-new-guest")
        && !this.wrongInvitation && this.valid;
    }

    get wrongInvitation(): boolean {
      return this.invitation?.timeLimited
        && !(this.invitation.startDate && this.invitation.endDate);
    }

    private mounted(): void {
        this.reset();
      }

    private reset(): void {
      this.selectedGuestGroup = null;
      this.newContact = {};
      if (this.canDoInEnvCompany("READ_CONTACTS")) {
        this.searchGuestBy = "search-guest";
      } else if (this.canDoInEnvCompany("CREATE_CONTACTS")) {
        this.searchGuestBy = "create-new-guest";
      }
      this.invitation = {
        countLimited: true,
        timeLimited: false,
        notificationType: "email",
        startDate: (new Date()).toISOString(),
      };
    }

    private async fetchCompanyUsers(): Promise<any[]> {
      this.loadingUsers = true;

      const p: Array<Promise<any>> = [];

      const queryParams = {
        search: this.search,
        groups: true,
        page: this.page,
        itemsPerPage: this.itemsPerPage,
      };

      if (this.canDoInEnvCompany("READ_CONTACTS")) {
        if (this.userTypes.includes("user")) {
          p.push(
            this.$store.dispatch("companies/getContacts", {
              id: this.envCompanyId,
              query: queryParams,
            }),
          );
        }

        if (this.userTypes.includes("contacts")) {
          p.push(
            this.$store.dispatch("companies/getUserGroups", {
              id: this.envCompanyId,
              query: {
                ...queryParams,
                type: "contacts",
              },
            }),
          );
        }
      }

      if (this.canDoInEnvCompany("READ_SUBORDINATES")) {
        if (this.userTypes.includes("user")) {
          p.push(
            this.$store.dispatch("companies/getSubordinates", {
              id: this.envCompanyId,
              query: queryParams,
            }),
          );
        }
      }

      if (!p.length) {
        this.loadingUsers = false;
        return;
      }

      try {
        const res = await Promise.all(p);

        this.totalCount = res.reduce(
          (acc, r) => acc + (r.totalCount || r.count || 0),
          0,
        );

        return res
          .flatMap((r) => [...(r.list || []), ...(r.users || [])])
          .map((item: any) => ({ ...item }));

      } finally {
        this.loadingUsers = false;
      }
    }

    private async getCompanyUsers(): Promise<void> {
      this.page = 1;
      this.users = [];
      this.users = await this.fetchCompanyUsers();
    }

    private async getNextCompanyUsers(): Promise<void> {
      this.page += 1;
      const newUsers = await this.fetchCompanyUsers();
      this.users = [...this.users, ...newUsers];
    }

    private chooseSearchGuestBy(): void {
      if (this.searchGuestBy === "create-new-guest") {
        this.selectedGuestGroup = null;
        this.newContact = {};
      } else {
        this.newContact = null;
      }
    }

    private validEmail(email): boolean {
      return formRules.isEmail(email);
    }

    private validPhoneNumber(phone): boolean {
      return regex.validPhoneNumber.test(phone);
    }

    private async addGuest(): Promise<void> {
      this.loadingAddGuest = true;
      const contactData = {...this.newContact};
      // Format data
      if (!contactData.email && contactData.phone) {
        contactData.email = contactData.phone;
      }
      if (!this.invitation?.timeLimited) {
        this.invitation.startDate = null;
        this.invitation.endDate = null;
      } else {
        this.invitation.startDate =
          this.invitation.startDate.substring(0, 16);
        this.invitation.endDate =
          this.invitation.endDate.substring(0, 16);
      }
      this.invitation.acceptOverPrice =  this.acceptOverPriceDialog;
      // Check if guest is already invited and show the duplicate guest modal
      const isDuplicateGuest: boolean = this.selectedGuestGroup
        ? await this.checkDuplicatedGuest(this.selectedGuestGroup)
        : false;
      if (isDuplicateGuest) {
        if (this.targetType === "keys") {
          // For unique key, open reminder modal
          this.showDuplicateModal = true;
        } else {
          // For keys groups and non-transferables, display error
          this.$store.commit("alerts/displayError", {
            msg: this.$t("key.guest.duplicate.error"),
          });
        }
        this.loadingAddGuest = false;
        return ;
      }
      // Send request
      const module = this.targetType;
      const payload: any = {
        id: this.targetId,
        invitation: this.invitation,
      };
      if (this.selectedGuestGroup) {
        payload.userGroupsIds = [this.selectedGuestGroup.id];
      }
      if (contactData && Object.keys(contactData).length > 0) {
        payload.userGroupsData = [contactData];
      }
      await this.$store.dispatch(`${module}/addGuests`, payload)
        .then((res) => {
          if (res?.notifications?.some((notif) => notif?.status === "rejected")) {
            this.$store.commit("alerts/displayWarning", {
              icon: "icon-picto_mes-contacts",
              msg: this.$t("key.guestAddWithoutNotif"),
            });
          } else {
            this.$store.commit("alerts/displaySuccess", {
              icon: "icon-picto_mes-contacts",
              msg: this.$t("key.guestAdd"),
            });
          }
          // Return the user group added to the key
          this.$emit("success", res.keyAccesses[0]);
        })
        .catch((error) => {
          switch (error?.response?.data?.message) {
            case "smsLimitReached":
              this.acceptOverPriceDialog = true;
              this.overpriceType = "smsLimitReached";
              break;
            case "smsOverpriceLimitReached":
              this.acceptOverPriceDialog = true;
              this.overpriceType = "smsOverpriceLimitReached";
              break;
            case "freemium":
              this.$store.commit("alerts/displayError", {
                msg: this.$i18n.t("invitation.freemium"),
              });
              break;
            case "duplicatedInvitation":
              this.$store.commit("alerts/displayError", {
                msg: this.$i18n.t("key.guest.duplicate.title"),
              });
              this.closeAcceptOverpriceDialog();
              break;
            default:
              this.$emit("reset");
              this.$store.commit("alerts/displayHttpError", { error });
              break;
          }
        })
        .finally(() => {
          this.loadingAddGuest = false;
        });
    }

    private async checkDuplicatedGuest(guestGroup: any): Promise<boolean> {
      const payload = {
        targetId: this.targetId,
        targetType: this.targetType,
        userId: guestGroup.type === "user" ? guestGroup.Users.find((user) => !!user.id)?.id : undefined,
        userGroupId: guestGroup.type !== "user" ? guestGroup.id : undefined,
      };
      const existingKeyAccesses: any[] = await this.$store.dispatch("keyAccesses/getByEntities", payload)
        .catch(() => 0);
      return existingKeyAccesses.length > 0;
    }

    private closeAcceptOverpriceDialog() {
        this.acceptOverPriceDialog = false;
      }

    // Action "No" of the duplicate guest Modal
    private closeDuplicateModal(): void {
      if (this.selectedGuestGroup) {
        this.$emit("success", { ...this.selectedGuestGroup, userGroupId: this.selectedGuestGroup.id });
      }
      this.loadingAddGuest = false;
      this.showDuplicateModal = false;
      this.selectedGuestGroup = null;
      this.newContact = {};
    }

    // Action "Yes" of the duplicate guest Modal
    private openKeyGuestReminder(): void {
      this.keyGuestReminderUsers = this.selectedGuestGroup.Users;
      this.showDuplicateModal = false;
      this.keyGuestReminderDialog = true;
    }

    private remindedGuest(user: any): void {
      this.$emit("success", {
        type: "user",
        Users: [user],
        userGroupId: this.selectedGuestGroup?.id,
      });
      this.closeKeyGuestReminder();
    }

    private closeKeyGuestReminder(): void {
      this.loadingAddGuest = false;
      this.keyGuestReminderDialog = false;
      this.keyGuestReminderUsers = [];
    }

    private getUserGroupPictureSrc(group: any) {
      return group?.type === "user"
        ? group?.Users?.[0]?.picturePath
        : null;
    }

    private getUserGroupPictureDefaultImg(group: any) {
      return group?.type === "user"
        ? "user"
        : "user-group";
    }
}
